import { createAuthClient } from "better-auth/react";
import { adminClient, emailOTPClient, oneTapClient, usernameClient } from "better-auth/client/plugins";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../store/hooks";
import { setUser, setLoading, clearUser, BetterAuthUserSchema } from "../store/slices/userSlice";
import { z } from 'zod';

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const baseAuthUrl = `${window.location.origin}/api/auth`;

export const authClient = createAuthClient({
	baseURL: baseAuthUrl,
	plugins: [
		adminClient(),
		usernameClient(),
		emailOTPClient(),
		...(googleClientId ? [oneTapClient({
			clientId: googleClientId,
			autoSelect: false,
			cancelOnTapOutside: true,
			context: "signin"
		})] : [])
	],
	fetchOptions: {
		credentials: "include",
		onError(context) {
			console.error('Auth error:', context.error);
		}
	}
});

// Export hooks directly for easier use
export const { useSession } = authClient;

// Console log prefix for connectivity-recovery transitions (greppable during support).
const CONNECTIVITY_LOG_PREFIX = '[connectivity]';

// Backoff schedule for retrying the session fetch while the backend is unreachable.
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 30000;

/**
 * better-auth's useSession() atom (session-atom.ts) already keeps the previous
 * `data` on a network/fetch error and only forces `data: null` when the server
 * responded 401 (a real "you are not authenticated" answer). We rely on that:
 * - error present && error.status === 401 -> genuine unauthenticated -> log out.
 * - error present && status !== 401 (network error, timeout, 5xx, no response)
 *   -> backend is unreachable, NOT "logged out". Keep the last known user and
 *     retry the session fetch with backoff until it recovers.
 * - no error, isPending false -> fetch actually succeeded; only then treat an
 *   empty `session` as a true "no session" and clear the user.
 */
const isConnectivityError = (error: { status?: number } | null | undefined): boolean => {
	if (!error) return false;
	return error.status !== 401;
};

// Helper hook to get user data directly with proper typing and sync to Redux
export const useUser = () => {
	const dispatch = useAppDispatch();
	const { data: session, isPending, error, refetch } = useSession();

	const [isReconnecting, setIsReconnecting] = useState(false);
	const wasOfflineRef = useRef(false);
	const retryAttemptRef = useRef(0);

	const connectivityError = isConnectivityError(error);

	useEffect(() => {
		if (isPending) {
			dispatch(setLoading(true));
			return;
		}

		if (connectivityError) {
			// Backend unreachable: keep whatever user we already know about.
			if (!wasOfflineRef.current) {
				wasOfflineRef.current = true;
				console.warn(`${CONNECTIVITY_LOG_PREFIX} connection lost, keeping last known session`, error);
			}
			setIsReconnecting(true);
			return;
		}

		if (wasOfflineRef.current) {
			wasOfflineRef.current = false;
			retryAttemptRef.current = 0;
			console.info(`${CONNECTIVITY_LOG_PREFIX} connection restored`);
		}
		setIsReconnecting(false);

		if (session?.user) {
			dispatch(setUser(session.user as z.infer<typeof BetterAuthUserSchema>));
		} else {
			// Fetch succeeded and genuinely returned no session -> real logout.
			dispatch(clearUser());
		}
	}, [session, isPending, connectivityError, error, dispatch]);

	// Auto-recover: retry the session fetch with exponential backoff while
	// the backend stays unreachable. Generic connectivity recovery, not
	// specific to this one auth endpoint.
	useEffect(() => {
		if (!connectivityError || isPending) {
			return;
		}

		const attempt = retryAttemptRef.current + 1;
		const delay = Math.min(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), RETRY_MAX_DELAY_MS);
		console.warn(`${CONNECTIVITY_LOG_PREFIX} retry attempt ${attempt} in ${delay}ms`);

		const timer = setTimeout(() => {
			retryAttemptRef.current = attempt;
			refetch?.();
		}, delay);

		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- retry loop keyed off error identity, not `refetch` stability
	}, [connectivityError, error, isPending]);

	return {
		user: session?.user as z.infer<typeof BetterAuthUserSchema> | null,
		isLoading: isPending,
		isReconnecting,
		error,
		retry: () => refetch?.(),
	};
};
